import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth } from "../contexts/AuthContext";
import {
  ClipboardEdit,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  Loader2,
  FileText
} from "lucide-react";
import { Link } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const EditRequests = () => {
  const { canApproveEdits } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [statusFilter, setStatusFilter] = useState("pending");
  
  const [actionDialog, setActionDialog] = useState(null);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", pagination.page);
      params.set("limit", pagination.limit);
      if (statusFilter) params.set("status", statusFilter);
      
      const response = await axios.get(`${API_URL}/edit-requests?${params.toString()}`);
      setRequests(response.data.data);
      setPagination(prev => ({
        ...prev,
        total: response.data.total
      }));
    } catch (error) {
      toast.error("Eroare la încărcarea cererilor");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [pagination.page, statusFilter]);

  const handleAction = async (action) => {
    if (!actionDialog) return;
    
    setSaving(true);
    try {
      await axios.put(`${API_URL}/edit-requests/${actionDialog}`, {
        action,
        comment
      });
      toast.success(action === "approve" ? "Cerere aprobată" : "Cerere respinsă");
      setActionDialog(null);
      setComment("");
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la procesarea cererii");
    } finally {
      setSaving(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
      case "rejected":
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-amber-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-700",
      approved: "bg-emerald-100 text-emerald-700",
      rejected: "bg-red-100 text-red-700"
    };
    const labels = {
      pending: "În așteptare",
      approved: "Aprobat",
      rejected: "Respins"
    };
    return (
      <Badge className={styles[status] || styles.pending}>
        {getStatusIcon(status)}
        <span className="ml-1">{labels[status] || status}</span>
      </Badge>
    );
  };

  const formatChanges = (changes) => {
    return Object.entries(changes).map(([key, value]) => (
      <div key={key} className="flex justify-between text-sm">
        <span className="text-slate-500">{key}:</span>
        <span className="text-slate-900 font-medium">{value}</span>
      </div>
    ));
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Cereri de Editare</h1>
          <p className="text-slate-500 mt-1">
            Gestionează cererile de modificare de la agenți
          </p>
        </div>
        
        <Select value={statusFilter || "all"} onValueChange={(val) => setStatusFilter(val === "all" ? "" : val)}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filtrează după status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">În așteptare</SelectItem>
            <SelectItem value="approved">Aprobate</SelectItem>
            <SelectItem value="rejected">Respinse</SelectItem>
            <SelectItem value="all">Toate</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Requests List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      ) : requests.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <ClipboardEdit className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Nu există cereri</h3>
              <p className="text-slate-500">Nu sunt cereri de editare {statusFilter ? `cu status "${statusFilter}"` : ""}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <Card key={request.id} data-testid={`request-${request.id}`}>
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                        <ClipboardEdit className="h-5 w-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{request.requested_by_name}</p>
                        <p className="text-sm text-slate-500">
                          {new Date(request.created_at).toLocaleString("ro-RO")}
                        </p>
                      </div>
                      {getStatusBadge(request.status)}
                    </div>
                    
                    <div className="ml-13 space-y-3">
                      {request.sale && (
                        <Link to={`/sales/${request.sale_id}`} className="text-sky-600 hover:underline text-sm">
                          <FileText className="h-4 w-4 inline mr-1" />
                          Vânzare: {request.sale.nr_contract_enel || request.sale_id.slice(0, 8)}
                        </Link>
                      )}
                      
                      <div className="bg-slate-50 rounded-lg p-4">
                        <p className="text-sm font-medium text-slate-700 mb-2">Modificări solicitate:</p>
                        {formatChanges(request.requested_changes)}
                      </div>
                      
                      <div>
                        <p className="text-sm font-medium text-slate-700">Motiv:</p>
                        <p className="text-sm text-slate-600">{request.reason}</p>
                      </div>
                      
                      {request.review_comment && (
                        <div className="bg-slate-50 rounded-lg p-4">
                          <p className="text-sm font-medium text-slate-700">Comentariu revizie:</p>
                          <p className="text-sm text-slate-600">{request.review_comment}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {request.status === "pending" && canApproveEdits() && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => setActionDialog(request.id)}
                        data-action="reject"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Respinge
                      </Button>
                      <Button
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => setActionDialog(request.id)}
                        data-action="approve"
                      >
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Aprobă
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.total > pagination.limit && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Afișare {((pagination.page - 1) * pagination.limit) + 1} -{" "}
            {Math.min(pagination.page * pagination.limit, pagination.total)} din{" "}
            {pagination.total} cereri
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page === 1}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page * pagination.limit >= pagination.total}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Action Dialog */}
      <Dialog open={!!actionDialog} onOpenChange={() => { setActionDialog(null); setComment(""); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Procesează Cererea</DialogTitle>
            <DialogDescription>
              Aprobă sau respinge cererea de editare
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Comentariu (opțional)</Label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Adaugă un comentariu..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setActionDialog(null); setComment(""); }}>
              Anulează
            </Button>
            <Button
              variant="outline"
              className="text-red-600 hover:text-red-700"
              onClick={() => handleAction("reject")}
              disabled={saving}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
              Respinge
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => handleAction("approve")}
              disabled={saving}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Aprobă
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EditRequests;
